import React, { useState } from 'react';
import { Grid, Typography, Chip, Button, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Lightbox from '../../components/Lightbox';
import { priceLabel, weightLabel, metalLabel, storeList } from './reviewFormat';

export default function LossList({ rows = [], onExplain, onUnexplain, onFound, repeatProductIds = new Set() }) {
    const { t } = useTranslation();
    const [zoomed, setZoomed] = useState(null);

    return (
        <>
            {rows.map(row => {
                const missedEverywhere = (row.searchedAt ?? []).length > 1;
                return (
                    <Grid
                        container
                        key={row.barcode}
                        alignItems='flex-start'
                        sx={{ gap: '.75rem', padding: '.75rem 0', borderBottom: '1px solid', borderColor: 'divider' }}
                    >
                        {row.imageUrl
                            ? <Box
                                component='img'
                                src={row.imageUrl}
                                alt={[row.productType, row.barcode].filter(Boolean).join(' ')}
                                role='button'
                                tabIndex={0}
                                onClick={() => setZoomed(row)}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault()
                                        setZoomed(row)
                                    }
                                }}
                                sx={{
                                    width: 72, height: 72, objectFit: 'cover', cursor: 'zoom-in', borderRadius: 1,
                                    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
                                }}
                            />
                            : <Box sx={{ width: 72, height: 72, bgcolor: 'action.hover', borderRadius: 1 }} />}

                        <Grid item xs>
                            <Typography sx={{ fontFamily: 'monospace', fontWeight: 500 }}>{row.barcode}</Typography>
                            <Typography color='text.secondary'>{row.productType}</Typography>
                            <Grid container sx={{ gap: '.75rem', marginTop: '.25rem' }}>
                                <Typography variant='body2'>{priceLabel(t, row.price)}</Typography>
                                <Typography variant='body2'>{metalLabel(t, row.metal)}</Typography>
                                <Typography variant='body2'>{weightLabel(t, row.weight)}</Typography>
                            </Grid>
                            <Typography variant='body2' color='text.secondary'>
                                {missedEverywhere
                                    ? t('inventoryCheck.review.bothStores')
                                    : t('inventoryCheck.review.searchedAt', { stores: storeList(row.searchedAt) })}
                                {(row.foundAt ?? []).length > 0
                                    && ` · ${t('inventoryCheck.review.foundAt', { stores: storeList(row.foundAt) })}`}
                                {row.who && ` · ${row.who}`}
                            </Typography>

                            <Grid container alignItems='center' sx={{ gap: '.5rem', marginTop: '.25rem' }}>
                                {repeatProductIds.has(String(row.productId)) && (
                                    <Chip size='small' color='warning' label={t('inventoryCheck.review.repeatFlag')} />
                                )}
                                {row.explanation && (<>
                                    <Chip size='small' label={t('inventoryCheck.review.explained', { reason: row.explanation.reason })} />
                                    {onUnexplain && (
                                        <Button size='small' onClick={() => onUnexplain(row.productId)}>
                                            {t('inventoryCheck.review.unexplain')}
                                        </Button>
                                    )}
                                </>)}
                                {!row.explanation && onExplain && (
                                    <Button size='small' onClick={() => onExplain(row.productId)}>
                                        {t('inventoryCheck.review.explain')}
                                    </Button>
                                )}
                                {!row.explanation && onFound && (
                                    <Button size='small' onClick={() => onFound(row.productId)}>
                                        {t('inventoryCheck.review.foundIt')}
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                );
            })}

            <Lightbox
                open={Boolean(zoomed)}
                onClose={() => setZoomed(null)}
                src={zoomed?.imageUrl}
                alt={zoomed ? `${zoomed.productType} ${zoomed.barcode}` : ''}
            />
        </>
    );
}
